import type { SkillSource } from "./index"

/**
 * Wave DOC (decision 0068, spec §4.2): a folder of Markdown, HTML (with its scripts, styles, fonts and images) and
 * Claude artifact links becomes the project's documents, and a re-import updates only what changed.
 */
export const importDocs: SkillSource = {
  name: "import-docs",
  title: "Import documents into a Minga project",
  description:
    "Import Markdown, HTML (with its scripts, styles, fonts and images), images, PDFs and Claude artifact links from this repo into a Minga project's documents, and keep them current: a re-import uploads only changed files and removes files that left the folder after a yes. Use it when the person asks to import, add or update docs, design notes, decisions or a Claude artifact in Minga.",
  body: `# Import documents into a Minga project

Every file becomes one document at its path (\`docs/design/x/README.md\`). Posting the same path again makes a new
version with the same ref (O-14); files whose bytes did not change are skipped. The bytes go from this machine to Minga
with \`curl\`, never through the chat.

## Steps

1. Ask which folders and which project. In the Minga repo the default is \`docs/design\`, \`docs/decisions\` and
   \`docs/investigations\`. Find the project with \`read_project\` and \`project\` = \`git remote get-url origin\`, or
   \`list_projects\`.
2. Call \`list_documents {project, under: "<folder>/"}\` for each folder and follow \`more\` until it is absent. Keep
   each document's \`path\` and \`sha256\`.
3. List the files with \`git ls-files -z -- <folders>\`, never \`find\` (it picks up untracked and ignored files).
   Keep \`.md\`, \`.html\`, \`.svg\`, \`.png\`, \`.jpg\`, \`.pdf\`, \`.js\`, \`.css\` and \`.woff2\`. Skip, and report,
   any path with a quote, a newline or a leading \`-\`.
4. Read the list with \`while IFS= read -r -d '' f; do …; done\`. Hash each file with \`shasum -a 256 -- "$f"\`. Always
   quote \`"$f"\`; never paste a path into a quoted string by hand.
5. Before posting anything, grep the files for secrets: private key blocks (\`BEGIN … PRIVATE KEY\`), \`sk-\`,
   \`sk-ant-\`, \`sk_live_\`, \`ghp_\`, \`github_pat_\`, \`xoxb-\`, \`AKIA\`, \`whsec_\`, JWTs (\`eyJ…\`), \`Bearer \`
   and lines like \`TOKEN=…\` or \`PASSWORD=…\`. On a hit, stop and ask the person.
6. Compare with step 2: a file whose path is listed with the same sha256 is unchanged; leave it out. Post the rest
   with \`post_document\` in calls of at most 50 rows: \`{path, file: {sha256, sizeBytes}, title}\`. The title is
   the first \`# \` heading of a Markdown file, the \`<title>\` of an HTML file, else the file name (left out, Minga
   takes it from the file the same way). When the repo has a GitHub remote, set \`url\` to
   \`https://github.com/<owner>/<repo>/blob/<default branch>/<path>\`: the repo stays the document's home.
7. The answer marks rows \`needs_upload\` and has one \`upload\` block for the call. Upload every \`missing\` file in
   one loop: \`curl -sS --fail -T "$f" "$base/$sha.$ext"\` (\`$base\` = \`upload.base\`, \`$ext\` = the file's
   extension). On HTTP 429 wait 60 seconds and go on. Then send the same rows again: they come back
   \`created\` or \`updated\`.
8. Documents under the folder in step 2 whose path is not in \`git ls-files\` any more: list them for the person, ask,
   and only after a yes post \`{path, remove: true}\` for each.
9. A Claude artifact: post \`{url, title}\` as a link (a claude.ai link is labelled and opens on claude.ai). For a view
   inside Minga, also read the artifact's files (Claude Code's Artifact tool, or unzip a Design export) and post them
   under one folder, each with \`url\` = the artifact link. If the Artifact read does not say "writer" (the person
   does not own it), set \`untrusted: true\` on those rows. Do the same for any content not read from this repo.
10. Short text you write yourself may go as \`content\` instead of \`file\` (at most 256 KB a row).
11. Report the counts (created, updated, unchanged, removed) and the \`page\` links of what changed, in a few lines.

## Never

- Paste file contents into the chat or into \`content\` when the file is on disk: upload it as \`file\`.
- Remove a document without the person's yes.
- Import files \`git ls-files\` does not list (untracked, ignored, .env, node_modules).
- Post a file that matched a secret pattern before the person said it is safe.
- Run Minga's links or uploads through any other host than the \`upload.base\` the answer gave.
`,
}
